import {
  decimalRules,
  getSpotAndGreeksEquity,
  getSpotAndGreeksForex,
} from 'components/common-form/builder/form/node/field/SpotAndGreeksField/SpotAndGreeks.helper';
import type { SPIntl } from 'contexts/intl/intlContext';
import type { IntlKey } from 'locales';
import type { Underlying } from 'models/underlying';
import type { FormDataStateMap } from 'state/formData';
import type { ProductNode } from 'state/product-description/model';
import type { GreeksTable } from 'state/quotes/details/details.model';
import { getExposedBatchFormStatus, getFormEnrichedData, getFormEnrichedUnderlyings } from 'state/selectors';
import type { AppState } from 'state/state.model';

export function getBatchFieldValuesForExport(
  formDataMap: FormDataStateMap,
  productNodes: ProductNode[],
  intl: SPIntl,
  state: AppState,
): Record<string, any> {
  let fieldValues: Record<string, any> = {};

  for (const formId in formDataMap) {
    fieldValues[formId] = {};

    for (const productNode of productNodes) {
      const newKey = intl.formatMessage({
        id: `form.${productNode.translationKey}` as IntlKey,
        defaultMessage: productNode.id,
      });

      if (formDataMap[formId].hasOwnProperty(productNode.id)) {
        switch (productNode.id) {
          case 'SpotAndGreeks':
            fieldValues = handleSpotAndGreeksField(state, formId, formDataMap, productNode, intl, fieldValues);
            break;
          default:
            fieldValues[formId][newKey] = formDataMap[formId][productNode.id];
        }
      } else if (productNode.id !== 'SpotAndGreeks') {
        fieldValues[formId][newKey] = '';
      }
    }

    fieldValues = handleFormStatusField(state, formId, intl.formatMessage, fieldValues);
    fieldValues = handleProductVariationField('ProductType', state, formId, intl.formatMessage, fieldValues);
    fieldValues = handleProductVariationField('ProductSubtype', state, formId, intl.formatMessage, fieldValues);
    fieldValues = handleProductVariationField('Wrapper', state, formId, intl.formatMessage, fieldValues);
  }

  return fieldValues;
}

function handleProductVariationField(
  productVariationType: 'ProductType' | 'ProductSubtype' | 'Wrapper',
  state: AppState,
  formId: string,
  formatMessage: SPIntl['formatMessage'],
  fieldValues: Record<string, any>,
): Record<string, any> {
  const enrichedData = getFormEnrichedData(state, formId);
  const productVariationTypeValue = enrichedData[productVariationType] as string;

  if (!productVariationTypeValue) {
    return fieldValues;
  }

  const key = formatMessage({
    id: `form.${productVariationType}` as IntlKey,
  });

  fieldValues[formId][key] = formatMessage({
    id: `quote.products.${productVariationTypeValue}` as IntlKey,
  });

  return fieldValues;
}

function handleFormStatusField(
  state: AppState,
  formId: string,
  formatMessage: SPIntl['formatMessage'],
  fieldValues: Record<string, any>,
): Record<string, any> {
  const formStatus = getExposedBatchFormStatus(state, formId);
  const formStatusKey = formatMessage({
    id: `form.FormStatus` as IntlKey,
  });
  fieldValues[formId][`${formStatusKey}`] = formatMessage({
    id: `batchPricer.form.status.${formStatus?.toLowerCase()}` as IntlKey,
  });
  return fieldValues;
}

function handleSpotAndGreeksField(
  state: AppState,
  formId: string,
  formDataMap: FormDataStateMap,
  productNode: ProductNode,
  intl: SPIntl,
  fieldValues: Record<string, any>,
): Record<string, any> {
  const underlyings = getFormEnrichedUnderlyings(state, formId);
  const greeksEntry = formDataMap[formId][productNode.id] as GreeksTable;
  if (greeksEntry.assetClass === 'Equity') {
    fieldValues = handleEquityGreeksEntry(greeksEntry, underlyings, intl, fieldValues, formId);
  }
  if (greeksEntry.assetClass === 'Forex') {
    fieldValues = handleForexGreeksEntry(greeksEntry, underlyings, intl, fieldValues, formId);
  }
  return fieldValues;
}

function handleEquityGreeksEntry(
  greeksEntry: GreeksTable,
  underlyings: Underlying[],
  intl: SPIntl,
  fieldValues: Record<string, any>,
  formId: string,
) {
  const equity = getSpotAndGreeksEquity(greeksEntry, underlyings);
  const underlyingKey = intl.formatMessage({
    id: `form.SingleUnderlying` as IntlKey,
  });
  const spotKey = intl.formatMessage({
    id: `form.SpotAndGreeks.Spot` as IntlKey,
  });
  const deltaPctKey = intl.formatMessage({
    id: `form.SpotAndGreeks.Delta` as IntlKey,
  });
  const deltaInShareKey = intl.formatMessage({
    id: `form.SpotAndGreeks.DeltaShares` as IntlKey,
  });
  const vegaKey = intl.formatMessage({
    id: `form.SpotAndGreeks.Vega` as IntlKey,
  });
  for (const [index, value] of equity.entries()) {
    const i = index + 1;
    fieldValues[formId][`${underlyingKey} ${i}`] = value.underlying?.id;
    fieldValues[formId][`${underlyingKey} ${i} - ${spotKey}`] = value.spot
      ? intl.formatNumber(value.spot, {
          minimumFractionDigits: decimalRules.Equity.spot.minimumFractionDigits,
          maximumFractionDigits: decimalRules.Equity.spot.maximumFractionDigits,
        })
      : '';
    fieldValues[formId][`${underlyingKey} ${i} - ${deltaPctKey}`] = value.deltaPct
      ? intl.formatNumber(value.deltaPct, {
          minimumFractionDigits: decimalRules.Equity.deltaPct.minimumFractionDigits,
          maximumFractionDigits: decimalRules.Equity.deltaPct.maximumFractionDigits,
        }) + ' %'
      : '';
    fieldValues[formId][`${underlyingKey} ${i} - ${deltaInShareKey}`] = value.deltaInShare
      ? intl.formatNumber(value.deltaInShare, {
          minimumFractionDigits: decimalRules.Equity.deltaInShare.minimumFractionDigits,
          maximumFractionDigits: decimalRules.Equity.deltaInShare.maximumFractionDigits,
        })
      : '';
    fieldValues[formId][`${underlyingKey} ${i} - ${vegaKey}`] = value.vega
      ? intl.formatNumber(value.vega, {
          minimumFractionDigits: decimalRules.Equity.vega.minimumFractionDigits,
          maximumFractionDigits: decimalRules.Equity.vega.maximumFractionDigits,
        }) + ' %'
      : '';
  }
  return fieldValues;
}

function handleForexGreeksEntry(
  greeksEntry: GreeksTable,
  underlyings: Underlying[],
  intl: SPIntl,
  fieldValues: Record<string, any>,
  formId: string,
) {
  const equity = getSpotAndGreeksForex(greeksEntry, underlyings);
  const underlyingKey = intl.formatMessage({
    id: `form.SingleUnderlying` as IntlKey,
  });
  const spotKey = intl.formatMessage({
    id: `form.SpotAndGreeks.Spot` as IntlKey,
  });
  for (const [index, value] of equity.entries()) {
    const i = index + 1;
    fieldValues[formId][`${underlyingKey} ${i} - ${spotKey}`] = value.spot
      ? intl.formatNumber(value.spot, {
          minimumFractionDigits: decimalRules.Forex.spot.minimumFractionDigits,
          maximumFractionDigits: decimalRules.Forex.spot.maximumFractionDigits,
        })
      : '';
    fieldValues[formId][`${underlyingKey} ${i} - ${getFxEntryHeader(value.fxDelta?.reference, value.underlying?.id)}`] =
      value.fxDelta?.value
        ? intl.formatNumber(value.fxDelta.value, {
            minimumFractionDigits: decimalRules.Forex.fxDelta.minimumFractionDigits,
            maximumFractionDigits: decimalRules.Forex.fxDelta.maximumFractionDigits,
          }) + ' %'
        : '';
    fieldValues[formId][`${underlyingKey} ${i} - ${getFxEntryHeader(value.fxVega?.reference, value.underlying?.id)}`] =
      value.fxVega?.value
        ? intl.formatNumber(value.fxVega.value, {
            minimumFractionDigits: decimalRules.Forex.fxVega.minimumFractionDigits,
            maximumFractionDigits: decimalRules.Forex.fxVega.maximumFractionDigits,
          }) + ' %'
        : '';
  }
  return fieldValues;
}

function getFxEntryHeader(value?: string, underlyingId?: string) {
  return value
    ? value
        .replace('(%)', '')
        .replace(underlyingId ?? '', '')
        .replace(/ +(?= )/g, '')
        .trim()
    : '';
}
